/* import __COLOCATED_TEMPLATE__ from './error.hbs'; */
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { action } from '@ember/object';
import Constants, { AMP_TYPES } from '../../utils/constants';
import Utils from '../../utils/utils';
import {
  findInSignalChain,
  addChartFn,
  getVoltRefErrors,
  getDacErrors,
  getOutAmpErrors,
  getInvertingAmpErrors,
} from '../../utils/error-utils';

export default class FastPrecisionError extends Component {
  @service intl;

  @action getErrorData() {
    let findInTopologies = (id) => this.args.model.topologyInputHandler.current.find((t) => t.config.id === id),
      findInResistors = (name) => this.args.model.resistorHandler.currentResistors.find((t) => t.name === name);

    const dacSelected = this.args.model.signalChainHandler.dac.selected,
      currentSignalChain = this.args.model.signalChainHandler.currentItems,
      voltRef = currentSignalChain.find((item) => item.isVoltageReference);

    let voltRefErrors = [];
    let outAmpErrors = [];
    let outAmpType = null;

    let outAmp = findInSignalChain(currentSignalChain, 'output amp');
    if (!outAmp) {
      outAmp = findInSignalChain(currentSignalChain, 'output buffer');
    }
    const outAmpTopology = findInTopologies('fp dac output');
    const outAmpResistor = findInResistors('output-amplifier') || {};

    const outAmpTypeId = outAmpTopology.selectedOption.id;
    if (outAmpTypeId === 'buffer') outAmpType = AMP_TYPES.BUFFER;
    if (outAmpTypeId === 'external resistors') outAmpType = AMP_TYPES.NON_INVERTING;

    // Voltage Reference
    if (voltRef) {
      voltRefErrors = getVoltRefErrors(voltRef.selected, dacSelected, this.args.model);
    }

    // DAC
    const dacOutputVoltage = Utils.isNumeric(this.args.model.positiveVoltage)
      ? this.args.model.positiveVoltage
      : this.args.model.signalChainHandler.inputVoltage;
    const dacErrors = getDacErrors(dacSelected, dacOutputVoltage, this.args.model, {
      getMultiplyingBwFromSettlingTime: true,
    });

    let nonInvertingAmpErrors = null;
    {
      const nonInvAmp = findInSignalChain(currentSignalChain, 'non inv amp'),
        nonInvAmpResistors = findInResistors('non-inverting-amplifier'); // check these out too...

      if (nonInvAmp && nonInvAmpResistors) {
        const nonInvertingAmpErrorsObj = getInvertingAmpErrors(
          AMP_TYPES.NON_INVERTING,
          nonInvAmp,
          this.args.model.signalChainHandler.inputVoltage, // ie output of Voltage Reference too
          dacSelected,
          this.args.model,
          nonInvAmpResistors,
        );
        nonInvertingAmpErrors = nonInvertingAmpErrorsObj.errors;
      }
    }

    let nonInvBufferErrors = null;
    {
      const nonInvBuffer = findInSignalChain(currentSignalChain, 'non inv buffer');
      const nonInvBufferResistors = findInResistors('non-inverting-amplifier') || {}; // this may be empty, but the buffer should still be handled

      if (nonInvBuffer) {
        const { errors } = getInvertingAmpErrors(
          AMP_TYPES.BUFFER,
          nonInvBuffer,
          this.args.model.positiveVoltage, // input voltage
          dacSelected,
          this.args.model,
          nonInvBufferResistors,
        );
        nonInvBufferErrors = errors;
      }
    }

    // Output Amplifier associated errors
    if (outAmpType) {
      const outAmpErrorsObj = getOutAmpErrors(
        outAmpType,
        outAmp,
        dacSelected,
        this.args.model,
        outAmpResistor,
        dacOutputVoltage,
        outAmpType !== AMP_TYPES.BUFFER ? true : false,
      );
      outAmpErrors = outAmpErrorsObj.outAmpErrors;
    }

    let chartInfo = { charts: [] };
    let addChart = addChartFn(chartInfo.charts);
    const outAmpOrBufferLabel =
      outAmpType == AMP_TYPES.BUFFER
        ? this.intl.t('labels.parts.output_buffer')
        : this.intl.t('labels.parts.output_amplifier');
    const outAmpOrBufferClass =
      outAmpType == AMP_TYPES.BUFFER ? Constants.SVG_PARTS.output_buffer : Constants.SVG_PARTS.output_amplifier;
    addChart(this.intl.t('labels.parts.fast_precision_dac'), Constants.SVG_PARTS.fast_precision_dac, dacErrors);
    addChart(this.intl.t('labels.parts.voltage_reference'), Constants.SVG_PARTS.voltage_reference, voltRefErrors);
    addChart(
      this.intl.t('labels.parts.non_inverting_amplifier'),
      Constants.SVG_PARTS.non_inverting_amplifier,
      nonInvertingAmpErrors,
    );
    addChart(
      this.intl.t('labels.parts.non_inverting_buffer'),
      Constants.SVG_PARTS.non_inverting_buffer,
      nonInvBufferErrors,
    );
    if (outAmpType) {
      addChart(outAmpOrBufferLabel, outAmpOrBufferClass, outAmpErrors);
    }

    chartInfo.hasIdealVoltage = true;

    return chartInfo;
  }
}
